/**
 * Role-Based Access Control (RBAC) Types
 *
 * Defines organizational roles and their associated permissions.
 * This extends the basic auth system with fine-grained permission controls.
 *
 * Implementation: HOME-12 RBAC requirement
 */

/**
 * User roles within an organization/tenant.
 *
 * Hierarchy (highest to lowest):
 * - org_owner: Organization owner (founder, highest privilege)
 * - org_admin: Organization administrator (full admin access)
 * - team_admin: Team administrator (can manage team members)
 * - member: Regular team member (standard user)
 * - viewer: Read-only access (limited interaction)
 * - agent: AI agent (programmatic access only)
 */
export type UserRole =
  | "org_owner"
  | "org_admin"
  | "team_admin"
  | "member"
  | "viewer"
  | "agent";

/**
 * Permission interface defining what actions a role can perform.
 *
 * Each permission controls a specific capability in the UI/API.
 * False means the action is disabled/hidden for that role.
 */
export interface Permission {
  /** Can create new boards */
  canCreateBoard: boolean;
  /** Can delete boards (usually admin-only) */
  canDeleteBoard: boolean;
  /** Can invite new users to workspace */
  canInviteUsers: boolean;
  /** Can change LLM model selection */
  canChangeModel: boolean;
  /** Can upload files/attachments */
  canUploadFiles: boolean;
  /** Can access chat functionality */
  canAccessChat: boolean;
  /** Can publish workflow versions */
  canPublishWorkflow: boolean;
  /** Can view audit logs (admin feature) */
  canViewAuditLog: boolean;
  /** Can manage team settings and members */
  canManageTeam: boolean;
}

/**
 * Permission mapping for each role.
 *
 * This is the single source of truth for RBAC enforcement.
 * Frontend uses this for UI gating, backend should enforce separately.
 */
export const ROLE_PERMISSIONS: Record<UserRole, Permission> = {
  org_owner: {
    canCreateBoard: true,
    canDeleteBoard: true,
    canInviteUsers: true,
    canChangeModel: true,
    canUploadFiles: true,
    canAccessChat: true,
    canPublishWorkflow: true,
    canViewAuditLog: true,
    canManageTeam: true,
  },
  org_admin: {
    canCreateBoard: true,
    canDeleteBoard: true,
    canInviteUsers: true,
    canChangeModel: true,
    canUploadFiles: true,
    canAccessChat: true,
    canPublishWorkflow: true,
    canViewAuditLog: true,
    canManageTeam: true,
  },
  team_admin: {
    canCreateBoard: true,
    canDeleteBoard: false, // Team admins can't delete boards (org-level action)
    canInviteUsers: true,
    canChangeModel: true,
    canUploadFiles: true,
    canAccessChat: true,
    canPublishWorkflow: true,
    canViewAuditLog: false, // Audit logs are org-admin only
    canManageTeam: true,
  },
  member: {
    canCreateBoard: true,
    canDeleteBoard: false,
    canInviteUsers: false, // Members can't invite (admin feature)
    canChangeModel: true,
    canUploadFiles: true,
    canAccessChat: true,
    canPublishWorkflow: true,
    canViewAuditLog: false,
    canManageTeam: false,
  },
  viewer: {
    canCreateBoard: false, // Viewers are read-only
    canDeleteBoard: false,
    canInviteUsers: false,
    canChangeModel: false, // Can't change model settings
    canUploadFiles: false, // Can't upload files
    canAccessChat: true,   // Can read chat (but ChatInput may limit interaction)
    canPublishWorkflow: false,
    canViewAuditLog: false,
    canManageTeam: false,
  },
  agent: {
    canCreateBoard: false, // Agents don't create boards via UI
    canDeleteBoard: false,
    canInviteUsers: false,
    canChangeModel: false,
    canUploadFiles: false,
    canAccessChat: true,   // Agents interact via chat/API
    canPublishWorkflow: false,
    canViewAuditLog: false,
    canManageTeam: false,
  },
};

/**
 * Type guard to check if a value is a valid UserRole.
 *
 * Useful for validating user input or API responses.
 */
export function isUserRole(value: unknown): value is UserRole {
  return (
    value === "org_owner" ||
    value === "org_admin" ||
    value === "team_admin" ||
    value === "member" ||
    value === "viewer" ||
    value === "agent"
  );
}
